import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Search,
  ChevronDown,
  Clock,
  Download,
  FileSpreadsheet,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  useGetAdminDailyReportQuery,
  useGetAdminStatisticsQuery,
  useGetAdminAttendanceReportQuery,
  useLazyGetAdminAttendanceReportQuery,
} from "../../slices/attendanceApiSlice";
import {
  useGetAcademyQuery,
  useGetDepartmentQuery,
  useGetDivisionsQuery,
} from "../../slices/userApiSlice";
import LateReasonDialog from "../../components/LateReasonDialog/LateReasonDialog";
import LocationCell from "../../components/Attendance/LocationCell";
import "../../pages/css/Feedback.css";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

export default function AdminEmployeeAttendance() {
  const navigate = useNavigate();
  const academySelectRef = useRef(null);
  const departmentSelectRef = useRef(null);

  const [activeTab, setActiveTab] = useState("clocked_in");
  const [currentPage, setCurrentPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedAcademyId, setSelectedAcademyId] = useState("all");
  const [selectedDepartmentId, setSelectedDepartmentId] = useState("all");
  const [showAcademyList, setShowAcademyList] = useState(false);
  const [showDepartmentList, setShowDepartmentList] = useState(false);
  const [showLateReasonDialog, setShowLateReasonDialog] = useState(false);
  const [selectedLateReason, setSelectedLateReason] = useState("");
  const [reportStartDate, setReportStartDate] = useState("");
  const [reportEndDate, setReportEndDate] = useState("");
  const [triggerDownload, { isFetching: isDownloading }] = useLazyGetAdminAttendanceReportQuery();
  const pageSize = 20;

  // Fetch academies for the dropdown filter
  const { data: academiesData } = useGetAcademyQuery();
  const academies = academiesData || [];

  // Fetch departments - shared across all academies
  const { data: departmentsData } = useGetDepartmentQuery();
  const departments = departmentsData || [];

  // Fetch divisions - shared across all academies
  const { data: divisionsData } = useGetDivisionsQuery();
  const divisions = divisionsData || [];

  // Get today's date in YYYY-MM-DD format (local time)
  const today = new Date().toLocaleDateString("en-CA");

  // Map tab to status
  const statusMap = {
    clocked_in: "clocked_in",
    clocked_out: "clocked_out",
    absentees: "absent",
    on_leave: "on_leave",
  };

  // Fetch admin daily report
  const {
    data: reportData,
    isLoading,
    isFetching,
    error,
  } = useGetAdminDailyReportQuery({
    status: statusMap[activeTab],
    page: currentPage,
    size: pageSize,
    academyId: selectedAcademyId,
  });

  // Fetch report data for the report tab
  const {
    data: reportTabRawData,
    isFetching: isFetchingReportTab,
  } = useGetAdminAttendanceReportQuery(
    {
      start_date: reportStartDate,
      end_date: reportEndDate,
      academyId: selectedAcademyId,
    },
    { skip: activeTab !== "report" || !reportStartDate || !reportEndDate }
  );

  const reportTabData = useMemo(() => {
    if (!reportTabRawData) return [];
    return reportTabRawData;
  }, [reportTabRawData]);

  // Get academy name by ID
  const getAcademyName = (academyId) => {
    if (!academyId || !academies.length) return "N/A";
    const academy = academies.find(
      (acad) => acad.academyId === academyId || acad._id === academyId,
    );
    return academy?.name || "N/A";
  };

  // Get department name by ID
  const getDepartmentName = (departmentId) => {
    if (!departmentId || !departments.length) return "N/A";
    const department = departments.find(
      (dept) => dept.departmentId === departmentId || dept._id === departmentId,
    );
    return department?.name || "N/A";
  };

  // Get division name by ID
  const getDivisionName = (divisionId) => {
    if (!divisionId || !divisions.length) return "N/A";
    const division = divisions.find(
      (div) => div.divisionId === divisionId || div._id === divisionId,
    );
    return division?.name || "N/A";
  };

  // Filter data based on search query, academy filter, department filter, and tab
  const filteredContent = useMemo(() => {
    if (!reportData?.content) return [];

    let filtered = reportData.content;

    // Filter by department (frontend only - doesn't affect API)
    if (selectedDepartmentId && selectedDepartmentId !== "all") {
      filtered = filtered.filter(
        (employee) => employee.departmentId === selectedDepartmentId,
      );
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((employee) => {
        const fullName = `${employee.firstName || ""} ${employee.lastName || ""}`.toLowerCase();
        const email = (employee.email || "").toLowerCase();
        const employeeId = (employee.employeeId || "").toLowerCase();
        return (
          fullName.includes(query) ||
          email.includes(query) ||
          employeeId.includes(query)
        );
      });
    }

    // Extra safety: filter by tab semantics so rows don't leak between tabs
    filtered = filtered.filter((employee) => {
      if (activeTab === "clocked_in") {
        return !!employee.clockInTime;
      }
      if (activeTab === "clocked_out") {
        return !!employee.clockOutTime;
      }
      if (activeTab === "on_leave") {
        return !!employee.leaveDays;
      }
      if (activeTab === "absentees") {
        return (
          !employee.clockInTime && !employee.clockOutTime && !employee.leaveDays
        );
      }
      return true;
    });

    // Deduplicate by user per tab (avoid duplicates when API returns multiple)
    const uniqueMap = new Map();
    for (const emp of filtered) {
      const baseKey =
        emp.userId || emp.employeeId || emp.email || emp.attendanceId;
      if (!baseKey) continue;
      const key = `${activeTab}-${baseKey}`;
      if (!uniqueMap.has(key)) {
        uniqueMap.set(key, emp);
      }
    }

    return Array.from(uniqueMap.values());
  }, [
    reportData,
    searchQuery,
    selectedAcademyId,
    selectedDepartmentId,
    academies,
    departments,
    activeTab,
  ]);

  const formatLocation = (location) => {
    if (!location) return "-";
    if (location.city) return location.city;
    if (location.latitude && location.longitude) {
      return `${location.latitude.toFixed(4)}, ${location.longitude.toFixed(
        4,
      )}`;
    }
    return "-";
  };

  // Get user initials for avatar
  const getInitials = (firstName, lastName) => {
    const first = firstName?.charAt(0)?.toUpperCase() || "";
    const last = lastName?.charAt(0)?.toUpperCase() || "";
    return first + last || "U";
  };

  // Get full name
  const getFullName = (firstName, lastName) => {
    return `${firstName || ""} ${lastName || ""}`.trim() || "Unknown";
  };

  const handleBack = () => {
    navigate("/admin/attendance");
  };

  const handleDownloadReport = async () => {
    if (!reportStartDate || !reportEndDate) {
      alert("Please select both start and end dates for the report.");
      return;
    }

    try {
      const { data } = await triggerDownload({
        start_date: reportStartDate,
        end_date: reportEndDate,
        academyId: selectedAcademyId,
      });

      if (!data || data.length === 0) {
        alert("No attendance records found for the selected date range and academy.");
        return;
      }

      // Sort data by academy name and then by date
      const sortedData = [...data].sort((a, b) => {
        const acadA = getAcademyName(a.academyId);
        const acadB = getAcademyName(b.academyId);
        if (acadA !== acadB) return acadA.localeCompare(acadB);
        return new Date(a.attendanceDate) - new Date(b.attendanceDate);
      });

      // Generate CSV content
      const headers = [
        "Date",
        "Employee ID",
        "First Name",
        "Last Name",
        "Academy",
        "Department",
        "Status",
        "Clock In",
        "Clock Out",
        "Late Reason",
        "Leave Type",
        "Leave Days"
      ];

      const csvRows = sortedData.map(record => {
        return [
          record.attendanceDate,
          record.employeeId,
          record.firstName || "",
          record.lastName || "",
          getAcademyName(record.academyId),
          getDepartmentName(record.departmentId),
          record.attendanceStatus,
          record.clockInTime ? new Date(record.clockInTime).toLocaleTimeString() : "N/A",
          record.clockOutTime ? new Date(record.clockOutTime).toLocaleTimeString() : "N/A",
          record.lateReason || "",
          record.leaveType || "",
          record.leaveDays || ""
        ].map(val => `"${String(val).replace(/"/g, '""')}"`).join(",");
      });

      const csvString = [headers.join(","), ...csvRows].join("\n");
      const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `Attendance_Report_${reportStartDate}_to_${reportEndDate}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading report:", error);
      alert("An error occurred while generating the report. Please try again.");
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(0);
    setSearchQuery("");
    setSelectedDepartmentId("all");
  };

  // Reset department filter when academy changes
  useEffect(() => {
    setSelectedDepartmentId("all");
    setCurrentPage(0);
  }, [selectedAcademyId]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        academySelectRef.current &&
        !academySelectRef.current.contains(event.target)
      ) {
        setShowAcademyList(false);
      }
      if (
        departmentSelectRef.current &&
        !departmentSelectRef.current.contains(event.target)
      ) {
        setShowDepartmentList(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const totalFilteredElements = activeTab === "report" ? reportTabData.length : filteredContent.length;
  const totalPages = Math.ceil(totalFilteredElements / pageSize);
  const startIndex = currentPage * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedContent = activeTab === "report" 
    ? reportTabData.slice(startIndex, endIndex)
    : filteredContent.slice(startIndex, endIndex);

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card p-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBack}
            className="rounded-full"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-semibold">Daily Employee Attendance</h1>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b bg-card">
        <div className="flex px-6">
          {[
            { id: "clocked_in", label: "Clocked In" },
            { id: "clocked_out", label: "Clocked Out" },
            { id: "absentees", label: "Absentees" },
            { id: "on_leave", label: "On Leave" },
            { id: "report", label: "Attendance Report" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`px-4 py-3 font-medium text-sm transition-colors border-b-2 ${activeTab === tab.id
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="border-b bg-card p-4 flex flex-col md:flex-row gap-4">
        <div className="relative flex-1 md:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Academy Select */}
        <div ref={academySelectRef} className="relative min-w-[180px]">
          <div
            className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 hover:bg-accent cursor-pointer"
            onClick={() => setShowAcademyList(!showAcademyList)}
          >
            <span className="text-foreground">
              {selectedAcademyId === "all"
                ? "Academy"
                : getAcademyName(selectedAcademyId)}
            </span>
            <ChevronDown
              className="h-4 w-4 opacity-50"
              style={{
                transform: showAcademyList ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 200ms ease-in-out",
              }}
            />
          </div>
          {showAcademyList && (
            <div className="absolute left-0 top-[calc(100%+4px)] z-50 min-w-[180px] rounded-md border bg-popover p-1 text-popover-foreground shadow-md">
              <div
                className={`relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors ${selectedAcademyId === "all"
                    ? "bg-accent text-accent-foreground"
                    : "hover:bg-accent hover:text-accent-foreground"
                  }`}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedAcademyId("all");
                  setShowAcademyList(false);
                }}
              >
                All Academies
              </div>
              {academies.map((academy) => (
                <div
                  key={academy.academyId || academy._id}
                  className={`relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors ${selectedAcademyId === (academy.academyId || academy._id)
                      ? "bg-accent text-accent-foreground"
                      : "hover:bg-accent hover:text-accent-foreground"
                    }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedAcademyId(academy.academyId || academy._id);
                    setShowAcademyList(false);
                  }}
                >
                  {academy.name}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Department Select - Always show since departments are shared across all academies */}
        <div ref={departmentSelectRef} className="relative min-w-[180px]">
          <div
            className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 hover:bg-accent cursor-pointer"
            onClick={() => setShowDepartmentList(!showDepartmentList)}
          >
            <span className="text-foreground">
              {selectedDepartmentId === "all"
                ? "Department"
                : getDepartmentName(selectedDepartmentId)}
            </span>
            <ChevronDown
              className="h-4 w-4 opacity-50"
              style={{
                transform: showDepartmentList
                  ? "rotate(180deg)"
                  : "rotate(0deg)",
                transition: "transform 200ms ease-in-out",
              }}
            />
          </div>
          {showDepartmentList && (
            <div className="absolute left-0 top-[calc(100%+4px)] z-50 min-w-[180px] rounded-md border bg-popover p-1 text-popover-foreground shadow-md">
              <div
                className={`relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors ${selectedDepartmentId === "all"
                    ? "bg-accent text-accent-foreground"
                    : "hover:bg-accent hover:text-accent-foreground"
                  }`}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedDepartmentId("all");
                  setShowDepartmentList(false);
                }}
              >
                All Departments
              </div>
              {departments.map((dept) => (
                <div
                  key={dept.departmentId || dept._id}
                  className={`relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors ${selectedDepartmentId === (dept.departmentId || dept._id)
                      ? "bg-accent text-accent-foreground"
                      : "hover:bg-accent hover:text-accent-foreground"
                    }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedDepartmentId(dept.departmentId || dept._id);
                    setShowDepartmentList(false);
                  }}
                >
                  {dept.name}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Date Range and Download */}
        <div className="flex flex-wrap items-center gap-2 ml-auto">
          <div className={`flex items-center gap-2 border rounded-md px-2 py-1 ${activeTab === 'report' ? 'bg-primary/5 border-primary/20 ring-2 ring-primary/10' : 'bg-background'}`}>
            <span className="text-xs font-medium text-muted-foreground uppercase">Range:</span>
            <input
              type="date"
              value={reportStartDate}
              onChange={(e) => setReportStartDate(e.target.value)}
              className="bg-transparent border-none text-sm focus:ring-0 p-0 w-28"
            />
            <span className="text-muted-foreground">to</span>
            <input
              type="date"
              value={reportEndDate}
              onChange={(e) => setReportEndDate(e.target.value)}
              className="bg-transparent border-none text-sm focus:ring-0 p-0 w-28"
            />
          </div>
          <Button
            onClick={handleDownloadReport}
            disabled={isDownloading || !reportStartDate || !reportEndDate}
            className="flex items-center gap-2"
          >
            {isDownloading ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Download Report
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto p-6">
        <div className="rounded-md border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>User</TableHead>
                {activeTab === "report" && <TableHead>Date</TableHead>}
                <TableHead className="hidden sm:table-cell">Name</TableHead>
                <TableHead className="hidden sm:table-cell">
                  Department
                </TableHead>
                <TableHead className="hidden sm:table-cell">Division</TableHead>
                {activeTab !== "absentees" && activeTab !== "on_leave" && (
                  <>
                    <TableHead>Location</TableHead>
                    <TableHead>
                      {activeTab === "clocked_in"
                        ? "Clocked in at"
                        : activeTab === "clocked_out" ? "Clocked out at" : "Clock In / Out"}
                    </TableHead>
                  </>
                )}
                {activeTab === "on_leave" && (
                  <>
                    <TableHead>Type</TableHead>
                    <TableHead>Duration</TableHead>
                  </>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {activeTab === "report" && !reportStartDate && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-20">
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <FileSpreadsheet className="h-10 w-10 opacity-20" />
                      <p>Select a date range to generate the attendance report</p>
                    </div>
                  </TableCell>
                </TableRow>
              )}

              {((activeTab !== "report" && (isLoading || isFetching)) || (activeTab === "report" && isFetchingReportTab)) ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={activeTab === "report" ? 7 : (activeTab === "absentees" ? 4 : 6)}>
                      <div className="h-12 w-full animate-pulse rounded bg-muted/50" />
                    </TableCell>
                  </TableRow>
                ))
              ) : paginatedContent.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={activeTab === "report" ? 7 : (activeTab === "absentees" ? 4 : 6)}
                    className="h-32 text-center"
                  >
                    <div className="flex flex-col items-center gap-1 text-muted-foreground">
                      <p>No attendance records found.</p>
                      {activeTab === "report" && <p className="text-xs">Try adjusting your filters or date range.</p>}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedContent.map((record) => (
                  <TableRow
                    key={record.id || record.attendanceId || record.userId}
                    className="cursor-pointer hover:bg-muted/30 transition-colors"
                    onClick={() => {
                      if (record.attendanceId) {
                        navigate(`/admin/attendance/record/${record.attendanceId}`);
                      }
                    }}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary font-semibold text-xs shrink-0">
                          {getInitials(record.firstName, record.lastName)}
                        </div>
                        <div className="flex flex-col min-w-0">
                          <span className="font-medium text-sm sm:hidden truncate">
                            {getFullName(record.firstName, record.lastName)}
                          </span>
                          <span className="text-xs text-muted-foreground font-mono">
                            {record.employeeId}
                          </span>
                        </div>
                      </div>
                    </TableCell>
                    {activeTab === "report" && (
                      <TableCell className="text-sm font-medium">
                        {record.attendanceDate}
                      </TableCell>
                    )}
                    <TableCell className="hidden sm:table-cell text-sm">
                      {getFullName(record.firstName, record.lastName)}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell text-sm">
                      {getDepartmentName(record.departmentId)}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell text-sm">
                      {getDivisionName(record.divisionId)}
                    </TableCell>
                    {activeTab !== "absentees" && activeTab !== "on_leave" && (
                      <>
                        <TableCell>
                          <LocationCell
                            location={
                              activeTab === "clocked_out"
                                ? record.clockOutLocation
                                : record.clockInLocation
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1.5">
                            {activeTab === "report" ? (
                              <div className="space-y-1">
                                {record.clockInTime && (
                                  <div className="flex items-center gap-1.5 text-[11px] text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full w-fit font-medium">
                                    <Clock className="h-3 w-3" />
                                    {new Date(record.clockInTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  </div>
                                )}
                                {record.clockOutTime && (
                                  <div className="flex items-center gap-1.5 text-[11px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full w-fit font-medium">
                                    <Clock className="h-3 w-3" />
                                    {new Date(record.clockOutTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  </div>
                                )}
                                {!record.clockInTime && !record.clockOutTime && (
                                  <span className="text-xs text-muted-foreground italic">No entries</span>
                                )}
                              </div>
                            ) : (
                              <div className="flex items-center gap-1.5 text-sm font-medium">
                                <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                                {activeTab === "clocked_in"
                                  ? record.clockInTime
                                    ? new Date(record.clockInTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                    : "N/A"
                                  : record.clockOutTime
                                    ? new Date(record.clockOutTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                    : "N/A"}
                              </div>
                            )}
                            {record.lateReason && (
                              <Badge
                                variant="secondary"
                                className="w-fit cursor-pointer hover:bg-yellow-200 transition-colors bg-yellow-100 text-yellow-800 border-yellow-200 text-[10px] px-1.5 py-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedLateReason(record.lateReason);
                                  setShowLateReasonDialog(true);
                                }}
                              >
                                Late
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                      </>
                    )}
                    {activeTab === "on_leave" && (
                      <>
                        <TableCell>
                          <Badge variant="outline" className="text-[10px] uppercase tracking-wider">{record.leaveType || "N/A"}</Badge>
                        </TableCell>
                        <TableCell className="text-sm font-medium">
                          {record.leaveDays ? `${record.leaveDays} Day(s)` : "N/A"}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="border-t bg-card p-4 flex items-center justify-between">
          <div className="text-sm text-muted-foreground font-medium">
            {totalFilteredElements} Results
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
              disabled={currentPage === 0}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 7) {
                  pageNum = i;
                } else if (currentPage <= 3) {
                  pageNum = i;
                } else if (currentPage >= totalPages - 4) {
                  pageNum = totalPages - 7 + i;
                } else {
                  pageNum = currentPage - 3 + i;
                }
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setCurrentPage(pageNum)}
                    className="w-8 h-8 p-0"
                  >
                    {pageNum + 1}
                  </Button>
                );
              })}
              {totalPages > 7 && currentPage < totalPages - 4 && (
                <>
                  <span className="px-2 text-muted-foreground">...</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentPage(totalPages - 1)}
                    className="w-8 h-8 p-0"
                  >
                    {totalPages}
                  </Button>
                </>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1))
              }
              disabled={currentPage === totalPages - 1}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <LateReasonDialog
        show={showLateReasonDialog}
        onClose={() => {
          setShowLateReasonDialog(false);
          setSelectedLateReason("");
        }}
        lateReason={selectedLateReason}
      />
    </div>
  );
}
